/**
 * Phone and tablet navigation (< 1280px, where the header's pill nav is hidden): a bottom tab bar with the four top-level screens and
 * «More», which opens a sheet with the rest, the theme and sign-out. Five tabs at most, icon + label, the current one in
 * ink; the sheet dims what is behind it, closes on Escape, on the backdrop and on navigation, and keeps clear of the
 * home indicator (safe-area inset).
 */
import type { Me } from "@prop/contracts";
import { cn } from "@prop/ui";
import { Banknote, BookOpen, CandlestickChart, CircleHelp, LayoutGrid, LogIn, Menu, Target, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";

type Tab = { href: string; label: string; icon: ReactNode };
type Item = { href: string; label: string };

function useActive() {
  const [location] = useLocation();
  return (href: string) => (href === "/" ? location === "/" : location === href || location.startsWith(`${href}/`) || (href === "/accounts" && location === "/"));
}

export function MobileNav(props: {
  me: Me | null;
  labels: { accounts: string; terminal: string; programs: string; payouts: string; faq: string; signIn: string; more: string; close: string; menu: string };
  more: Item[];
  extra: ReactNode;
}) {
  const { me, labels } = props;
  const active = useActive();
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  // Close the sheet whenever the route changes.
  useEffect(() => setOpen(false), [location]);
  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open]);

  const tabs: Tab[] = me
    ? [
        { href: "/accounts", label: labels.accounts, icon: <LayoutGrid aria-hidden /> },
        { href: "/terminal", label: labels.terminal, icon: <CandlestickChart aria-hidden /> },
        { href: "/programs", label: labels.programs, icon: <Target aria-hidden /> },
        { href: "/payouts", label: labels.payouts, icon: <Banknote aria-hidden /> },
      ]
    : [
        { href: "/programs", label: labels.programs, icon: <Target aria-hidden /> },
        { href: "/faq", label: labels.faq, icon: <CircleHelp aria-hidden /> },
        { href: "/login", label: labels.signIn, icon: <LogIn aria-hidden /> },
      ];
  const moreOn = props.more.some((i) => active(i.href));

  return (
    <>
      <nav aria-label={labels.menu} className="fixed inset-x-0 bottom-0 z-header border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur xl:hidden">
        <ul className="mx-auto flex max-w-lg items-stretch justify-around">
          {tabs.map((tb) => {
            const on = active(tb.href);
            return (
              <li key={tb.href} className="flex-1">
                <Link
                  href={tb.href}
                  aria-current={on ? "page" : undefined}
                  className={cn("flex min-h-14 flex-col items-center justify-center gap-0.5 px-1 text-[11px] font-medium [&_svg]:size-5", on ? "text-foreground" : "text-muted-foreground")}
                >
                  <span className={cn("grid h-7 w-12 place-items-center rounded-full transition-colors", on && "bg-foreground text-background")}>{tb.icon}</span>
                  <span className="max-w-full truncate">{tb.label}</span>
                </Link>
              </li>
            );
          })}
          {props.more.length > 0 && (
            <li className="flex-1">
              <button
                type="button"
                aria-expanded={open}
                aria-haspopup="dialog"
                onClick={() => setOpen(true)}
                className={cn("flex min-h-14 w-full flex-col items-center justify-center gap-0.5 px-1 text-[11px] font-medium [&_svg]:size-5", moreOn || open ? "text-foreground" : "text-muted-foreground")}
              >
                <span className={cn("grid h-7 w-12 place-items-center rounded-full transition-colors", moreOn && "bg-foreground text-background")}>
                  <Menu aria-hidden />
                </span>
                <span>{labels.more}</span>
              </button>
            </li>
          )}
        </ul>
      </nav>
      {open && (
        <div className="fixed inset-0 z-modal xl:hidden" role="dialog" aria-modal="true" aria-label={labels.more}>
          <button type="button" aria-label={labels.close} className="absolute inset-0 h-full w-full bg-black/55 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto overscroll-contain rounded-t-3xl bg-card px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 shadow-2xl">
            <div className="mb-2 flex items-center justify-between">
              <span className="mx-auto h-1 w-10 rounded-full bg-muted-foreground/30" aria-hidden />
              <button type="button" onClick={() => setOpen(false)} className="absolute right-3 top-2 grid size-11 place-items-center rounded-full text-muted-foreground" aria-label={labels.close}>
                <X className="size-5" aria-hidden />
              </button>
            </div>
            <ul className="divide-y divide-border">
              {props.more.map((i) => (
                <li key={i.href}>
                  <Link href={i.href} aria-current={active(i.href) ? "page" : undefined} className={cn("flex min-h-12 items-center gap-3 py-3 text-base", active(i.href) ? "font-semibold" : "")}>
                    <BookOpen className="size-4 text-muted-foreground" aria-hidden />
                    {i.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">{props.extra}</div>
          </div>
        </div>
      )}
    </>
  );
}
